import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { EMPTY, catchError } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDialogModule, OuiDialogService } from 'omnium-ui/dialog';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { appRoutePaths } from '../../../../app.paths';
import { TechnicalInvestorPurgeExecution, TechnicalInvestorPurgeExecutionsPage } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { TechnicalInvestorPurgeExecutionDetailsDialogComponent } from './technical-investor-purge-execution-details-dialog.component';

@Component({
  selector: 'dbw-technical-investor-purge-executions-page',
  imports: [
    MatPaginatorModule,
    MatTableModule,
    RouterModule,
    OuiButtonModule,
    OuiDialogModule,
    OuiTableModule,
    OuiTagModule,
    StateCardComponent,
  ],
  templateUrl: './technical-investor-purge-executions.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './technical-investor-purge-executions.component.scss',
})
export class TechnicalInvestorPurgeExecutionsPageComponent {
  readonly displayedColumns = ['investor', 'request', 'status', 'requestedAt', 'actions'];
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly pageIndex = signal(0);
  readonly pageSize = signal(25);
  readonly loading = signal(true);
  readonly error = signal<string | undefined>(undefined);
  readonly page = signal<TechnicalInvestorPurgeExecutionsPage | undefined>(undefined);
  readonly appRoutePaths = appRoutePaths;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly dialogService: OuiDialogService
  ) {
    this.load();
  }

  reload(): void {
    this.load(true);
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.load(true);
  }

  openDetails(execution: TechnicalInvestorPurgeExecution): void {
    this.dialogService.openDialog(TechnicalInvestorPurgeExecutionDetailsDialogComponent, { execution }, undefined, '720px');
  }

  getStatusLabel(execution: TechnicalInvestorPurgeExecution): string {
    if (execution.status) {
      return execution.status;
    }

    return execution.isInProgress ? 'En cours' : 'Terminé';
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return 'Non renseigné';
    }

    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(new Date(value));
  }

  private load(forceLoadingIndicator = false): void {
    if (forceLoadingIndicator || !this.page()) {
      this.loading.set(true);
    }

    this.apiService
      .getTechnicalInvestorPurgeExecutionsPage(this.pageIndex(), this.pageSize())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          this.error.set(error?.error?.detail ?? 'Impossible de charger les demandes de purge.');
          this.loading.set(false);
          return EMPTY;
        })
      )
      .subscribe(page => {
        this.page.set(page);
        this.error.set(undefined);
        this.loading.set(false);
      });
  }
}
